/**
 * Reports a client-side form failure to the backend so it shows up in the
 * admin "Failed submissions" tile. Includes the Microsoft Clarity custom-id
 * tag so admins can deep-link to the recorded session.
 *
 * Safe-by-design:
 *   • Never throws — if the beacon endpoint itself is unreachable, we ignore.
 *   • Sanitizes the payload to the same allow-list the backend enforces.
 *   • Falls back gracefully if Clarity hasn't loaded yet.
 */
const ALLOWED_DRAFT_KEYS = new Set([
  'name', 'firstName', 'lastName', 'fullName',
  'email', 'phone', 'company',
  'subject', 'message', 'projectDescription', 'projectDetails',
  'industry', 'facilityType', 'projectType', 'projectSize',
  'squareFootage', 'location', 'timeline', 'budget', 'budgetRange',
  'helpType', 'callbackTime', 'contactMethod', 'region', 'timeZone',
  'designGoals', 'productInterest', 'industryType',
  'address', 'city', 'state', 'zip',
]);

const CLARITY_CUSTOM_ID_KEY = 'portico:clarity-custom-id';
const CLARITY_PROJECT_ID = 'x1zjxwfuce';

const pickDraft = (raw) => {
  if (!raw || typeof raw !== 'object') return {};
  const out = {};
  for (const [k, v] of Object.entries(raw)) {
    if (!ALLOWED_DRAFT_KEYS.has(k)) continue;
    if (typeof v === 'string') out[k] = v.slice(0, 4000);
    else if (Array.isArray(v)) out[k] = v.slice(0, 50).map((x) => String(x).slice(0, 4000));
    else if (v === null || ['number', 'boolean'].includes(typeof v)) out[k] = v;
  }
  return out;
};

const getOrCreateClarityCustomId = () => {
  if (typeof window === 'undefined') return null;
  try {
    let id = window.localStorage.getItem(CLARITY_CUSTOM_ID_KEY);
    if (!id) {
      id = `pf-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      window.localStorage.setItem(CLARITY_CUSTOM_ID_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
};

const tagClaritySession = (customId, formType) => {
  if (typeof window === 'undefined' || typeof window.clarity !== 'function') return;
  try {
    // Identify the visitor so the session is searchable by CustomId.
    window.clarity('identify', customId);
    // Fire a named event the admin can filter on inside the Clarity UI.
    window.clarity('event', 'form_submission_failed');
    window.clarity('set', 'failed_form_type', formType);
  } catch (err) {
    // Clarity SDK not loaded (blocked by ad blocker or consent decline) — non-actionable
    console.debug('[clarity] failure signal skipped:', err?.message);
  }
};

/**
 * Report a failed form submission.
 *
 * @param {object} args
 * @param {string} args.formType   - e.g. "contact", "quote", "fast_quote"
 * @param {object} args.draft      - current form data
 * @param {string} [args.errorReason]
 */
export const reportFailedSubmission = async ({ formType, draft = {}, errorReason }) => {
  const customId = getOrCreateClarityCustomId();
  tagClaritySession(customId, formType);

  const cleaned = pickDraft(draft);
  const payload = {
    formType,
    errorReason: errorReason ? String(errorReason).slice(0, 500) : 'client_error',
    pageUrl: typeof window !== 'undefined' ? window.location.href : null,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    clarityCustomId: customId,
    clarityProjectId: CLARITY_PROJECT_ID,
    name: cleaned.fullName || [cleaned.firstName, cleaned.lastName].filter(Boolean).join(' ') || cleaned.name || null,
    email: cleaned.email || null,
    phone: cleaned.phone || null,
    company: cleaned.company || null,
    draft: cleaned,
  };

  try {
    await fetch('/api/failed-submissions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      // Beacon-style: don't block the catch block on slow networks.
      keepalive: true,
    });
  } catch (err) {
    // Beacon failures are intentionally swallowed — the primary submit already
    // failed and surfaced its own error; this is best-effort telemetry.
    console.debug('[form-failure-beacon] POST failed:', err?.message);
  }
};

export default reportFailedSubmission;
